import React, { useState } from 'react';
import { Button } from '@mui/material';

import ColorPickerPopover from '../ColorPickerObjects';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import { ImageElementType } from '../../../redux/TabTile/TabStateInterfaces';
import '../ToolsBar.css';

interface EffectsProps {
  selectedImageId: string | null;
  imageElements: ImageElementType[];
  onUpdateImageElement: (id: string, updates: Partial<ImageElementType>) => void;
  iconColor?: string;
}

const DEFAULTS = {
  backgroundColor: '#ffffff',
  backgroundAlpha: 0,
  imageAlpha: 100,
  borderColor: '#000000',
  borderWidth: 0,
  borderRadius: 12,
};

const Effects: React.FC<EffectsProps> = ({
  selectedImageId,
  imageElements,
  onUpdateImageElement,
  iconColor,
}) => {
  const [bgColorAnchor, setBgColorAnchor] = useState<HTMLElement | null>(null);
  const [borderColorAnchor, setBorderColorAnchor] = useState<HTMLElement | null>(null);

  const selectedImage = imageElements.find((img) => img.id === selectedImageId);
  if (!selectedImage || !selectedImageId) {
    return <div className="optionsInfo">No image selected</div>;
  }

  const updateImage = (updates: Partial<ImageElementType>) => {
    onUpdateImageElement(selectedImageId, updates);
  };

  const hasModifiedValues = () =>
    selectedImage.backgroundColor !== DEFAULTS.backgroundColor ||
    selectedImage.backgroundAlpha !== DEFAULTS.backgroundAlpha ||
    selectedImage.imageAlpha !== DEFAULTS.imageAlpha ||
    selectedImage.borderColor !== DEFAULTS.borderColor ||
    selectedImage.borderWidth !== DEFAULTS.borderWidth ||
    selectedImage.borderRadius !== DEFAULTS.borderRadius;

  const resetImageProperties = () => {
    updateImage({ ...DEFAULTS });
  };

  return (
    <div className="optionsInfo">
      {/* Background color */}
      <div style={{ display: 'flex', paddingBottom: '8px', flexDirection: 'column' }}>
        <div
          className="optionDescription"
          style={{
            marginTop: '8px',
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            color: iconColor,
          }}
        >
          <span>Background Color</span>
          <div
            onClick={(e) => setBgColorAnchor(e.currentTarget)}
            style={{
              marginTop: '10px',
              height: '1.25rem',
              width: '100px',
              backgroundColor: selectedImage.backgroundColor || '#ffffff',
              border: '2px solid darkgray',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          />
        </div>
        <ColorPickerPopover
          isOpen={Boolean(bgColorAnchor)}
          anchorEl={bgColorAnchor}
          onClose={() => setBgColorAnchor(null)}
          currentColor={selectedImage.backgroundColor || '#ffffff'}
          onColorChange={(color) => updateImage({ backgroundColor: color })}
        />

        {/* Background Opacity */}
        <div>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Background Opacity
          </div>
          <SliderWithInput
            percent
            sliderValue={selectedImage.backgroundAlpha ?? 0}
            sliderMinMax={{ min: 0, max: 100, step: 1 }}
            changeValue={(val) => updateImage({ backgroundAlpha: Math.max(0, Math.min(100, val)) })}
          />
        </div>

        {/* Image Opacity */}
        <div>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Image Opacity
          </div>
          <SliderWithInput
            percent
            sliderValue={selectedImage.imageAlpha ?? 100}
            sliderMinMax={{ min: 0, max: 100, step: 1 }}
            changeValue={(val) => updateImage({ imageAlpha: Math.max(0, Math.min(100, val)) })}
          />
        </div>
      </div>

      {/* Border settings */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '20px',
          borderTop: '1px solid lightgray',
          paddingBottom: '8px',
        }}
      >
        <div
          className="optionDescription"
          style={{
            marginTop: '8px',
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            color: iconColor,
          }}
        >
          <span>Border Color</span>
          <div
            onClick={(e) => setBorderColorAnchor(e.currentTarget)}
            style={{
              marginTop: '10px',
              height: '1.25rem',
              width: '100px',
              backgroundColor: selectedImage.borderColor || '#000000',
              border: '2px solid darkgray',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          />
        </div>
        <ColorPickerPopover
          isOpen={Boolean(borderColorAnchor)}
          anchorEl={borderColorAnchor}
          onClose={() => setBorderColorAnchor(null)}
          currentColor={selectedImage.borderColor || '#000000'}
          onColorChange={(color) => updateImage({ borderColor: color })}
        />

        {/* Border width */}
        <div>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Border Width
          </div>
          <SliderWithInput
            percent={false}
            sliderValue={selectedImage.borderWidth ?? 0}
            sliderMinMax={{ min: 0, max: 30, step: 1 }}
            changeValue={(val) => updateImage({ borderWidth: Math.max(0, Math.min(30, val)) })}
          />
        </div>

        {/* Border radius */}
        <div>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Border Radius
          </div>
          <SliderWithInput
            sliderValue={selectedImage.borderRadius ?? 12}
            sliderMinMax={{ min: 0, max: 50, step: 1 }}
            changeValue={(val) => updateImage({ borderRadius: Math.max(0, Math.min(50, val)) })}
          />
        </div>

        {/* Reset Button */}
        <Button
          onClick={resetImageProperties}
          disabled={!hasModifiedValues()}
          variant="outlined"
          sx={{
            marginTop: '18px',
            marginLeft: '0.4rem',
            marginRight: '0.2rem',
            backgroundColor: 'transparent',
            color: '#2BB9BB',
            border: '1px solid #2BB9BB',
            '&:hover:not(:disabled)': {
              backgroundColor: '#229a9a',
              color: '#ffffff',
              borderColor: '#808080',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
              borderColor: '#e0e0e0',
            },
          }}
        >
          Reset Effects
        </Button>
      </div>
    </div>
  );
};

export default Effects;
